/**
 * Smart truncation utility that preserves both prefix and suffix of long strings.
 * Uses middle truncation to keep important context visible at both ends.
 * 
 * @param {string} value - The string to truncate
 * @param {number} maxChars - Maximum character length before truncation
 * @param {number} headRatio - Ratio of maxChars to use for prefix (default: 0.65)
 * @param {number} tailRatio - Ratio of maxChars to use for suffix (default: 0.25)
 * @returns {string} - Truncated string with ellipsis in the middle, or original if short enough
 * 
 * @example
 * smartTruncateMiddle("very-long-project-name-with-many-segments-prod", 40)
 * // Returns: "very-long-project-name-with…-prod"
 */
export function smartTruncateMiddle(value, maxChars, headRatio = 0.65, tailRatio = 0.25) {
  // Handle edge cases
  if (!value || typeof value !== 'string') {
    return value || '';
  }
  
  if (maxChars <= 0) {
    return '';
  }
  
  // If string is shorter than maxChars, return unchanged
  if (value.length <= maxChars) {
    return value;
  }
  
  // Ensure ratios are valid and don't exceed 1.0
  const safeHeadRatio = Math.max(0, Math.min(1, headRatio));
  const safeTailRatio = Math.max(0, Math.min(1, tailRatio));
  
  // Calculate character counts for head and tail
  // Reserve space for ellipsis (1 char)
  const availableChars = maxChars - 1;
  const headChars = Math.floor(availableChars * safeHeadRatio);
  const tailChars = Math.floor(availableChars * safeTailRatio);
  
  // Ensure we have at least 1 char for head and tail
  if (headChars < 1 || tailChars < 1) {
    // Fallback to simple truncation if maxChars is too small
    return value.slice(0, maxChars - 1) + '…';
  }
  
  // Extract head and tail
  const head = value.slice(0, headChars);
  const tail = value.slice(-tailChars);
  
  // Combine with ellipsis
  return `${head}…${tail}`;
}
